<i18n lang="yaml" src="@/locales/notifications.yaml"></i18n>

<template>
  <!-- groups -->
  <v-list-group
    :expand-icon="$icons.chevron_down"
    :collapse-icon="$icons.chevron_up"
    v-if="item.items"
    v-model="expanded"
  >
    <template v-slot:activator="{ props }">
      <v-list-item v-bind="props" slim class="mx-3">
        <template #prepend>
          <v-icon class="ml-2" size="small">{{ item.icon }}</v-icon>
        </template>
        <v-list-item-title class="d-flex align-center justify-space-between">
          {{ item.title }}
          <v-chip
            v-if="item.chip"
            :color="item.chip.color"
            size="x-small"
            class="float-right"
          >
            <v-icon v-if="item.chip.icon" size="x-small">{{
              item.chip.icon
            }}</v-icon>
            {{ item.chip.text }}
          </v-chip>
        </v-list-item-title>
      </v-list-item>
    </template>
    <MenuListItem
      v-for="subitem in visibleSubItems"
      :key="subitem.title"
      :item="subitem"
      :notifications="notifications"
      :level="level + 1"
      :chip="subitem.chip"
      @expand="expand"
    ></MenuListItem>
  </v-list-group>
  <!-- normal items -->
  <v-list-item
    v-else
    ref="item"
    slim
    :active="isActive(item)"
    :exact="false"
    density="default"
    class="celus-nav-item mx-3"
    :class="isActive(item) ? '' : 'on-nav-bar'"
    @change="change()"
    @click="handleClick(item)"
  >
    <template #prepend v-if="item.icon">
      <v-icon size="small" class="ml-2 opacity-100">{{ item.icon }}</v-icon>
    </template>
    <v-list-item-title
      class="d-flex align-center justify-space-between opacity-100"
    >
      {{ item.title }}
      <v-chip
        v-if="item.chip"
        :color="item.chip.color"
        variant="flat"
        size="x-small"
        class="float-right"
      >
        <v-icon v-if="item.chip.icon" size="small">{{ item.chip.icon }}</v-icon>
        {{ item.chip.title }}
      </v-chip>
      <v-tooltip
        location="bottom"
        v-if="item.linkTo in notifications"
        max-width="400"
      >
        <template v-slot:activator="{ props }">
          <v-icon
            v-bind="props"
            size="small"
            :color="notifications[item.linkTo].level"
            class="float-right"
          >
            {{
              notifications[item.linkTo].level === "warning"
                ? $icons.warning
                : $icons.info
            }}
          </v-icon>
        </template>
        <span
          v-html="$t('notifications.' + notifications[item.linkTo].tooltip)"
        ></span>
      </v-tooltip>
    </v-list-item-title>
  </v-list-item>
</template>

<script>
import { useRoute } from "vue-router";

export default {
  name: "MenuListItem",

  emits: ["expand"],

  props: {
    item: { required: true, type: Object },
    notifications: { required: true, type: Object },
    level: { default: 0, type: Number },
    chip: { default: null, type: Object },
  },

  data() {
    return {
      expanded: null,
      route: useRoute(),
    };
  },

  computed: {
    visibleSubItems() {
      return this.item.items.filter((item) => item.show ?? true);
    },
  },

  methods: {
    change() {
      if (this.$refs.item.isActive) {
        this.$emit("expand");
      }
    },
    expand() {
      this.expanded = true;
    },

    isActive(item) {
      return this.route.path.startsWith(
        this.$router.resolve({ name: item.linkTo }).href,
      );
    },
    async handleClick(item) {
      if (this.isActive(item)) {
        await this.$router
          .push({
            name: item.linkTo,
            query: { _t: Date.now() },
          })
          .catch(() => {});
      } else {
        await this.$router.push({ name: item.linkTo });
      }
    },
  },
};
</script>

<style lang="scss">
.v-list-item__append {
  font-size: x-small;
}

.v-list-group__items {
  --indent-padding: 32px;
}

/* Active wash lives on Vuetify's overlay, which has no color/opacity props.
   Themes can override the active colors with the `nav-active-color`,
   `nav-active-text-color` and `nav-active-icon-color` theme variables
   (plugins/vuetify.js); by default the item uses primary-container and the
   icon follows the label color. */
.celus-nav-item.v-list-item--active {
  --celus-nav-active-text: var(
    --v-nav-active-text-color,
    var(--v-theme-on-primary-container)
  );

  color: rgb(var(--celus-nav-active-text));

  .v-list-item__prepend .v-icon {
    color: rgb(var(--v-nav-active-icon-color, var(--celus-nav-active-text)));
  }
}

/* Same corners as cards in every theme (the per-theme card-border-radius
   variable, see styles/settings.scss); the active overlay inherits them. */
.celus-nav-item {
  border-radius: var(--v-card-border-radius, 4px);
}

.celus-nav-item .v-list-item__prepend,
.celus-nav-item .v-list-item__content {
  position: relative;
  z-index: 1;
}

/* The warning icon is unreadable on light active washes; themes can darken it
   by setting `nav-active-warning-mix` (share of warning kept, the rest is black).
   The !important beats Vuetify's text-warning. */
.celus-nav-item.v-list-item--active .v-icon.text-warning {
  color: color-mix(
    in srgb,
    rgb(var(--v-theme-warning)) var(--v-nav-active-warning-mix, 100%),
    black
  ) !important;
}

.celus-nav-item.v-list-item--active > .v-list-item__overlay {
  background-color: rgb(
    var(--v-nav-active-color, var(--v-theme-primary-container))
  );
  opacity: 1;
}
</style>
